/**
 * ViewModel: Reward Catalog Create/Edit
 * Presentation logic for reward catalog create/edit feature.
 */

/* eslint-disable react-refresh/only-export-components */

import type { Dispatch, SetStateAction } from "react";
import {
  RewardType,
  VoucherStatus,
  type CreateVoucherPayload,
  type UpdateVoucherPayload,
  type VoucherDetail,
  type CreateEWalletPayload,
  type CreatePointConversionPayload,
  type ImportVoucherkuPayload,
} from "../../domain/model/RewardCatalogModel";
import { CreateVoucherUseCase } from "../../domain/usecase/CreateVoucherUseCase";
import { UpdateVoucherUseCase } from "../../domain/usecase/UpdateVoucherUseCase";
import { FetchVoucherDetailUseCase } from "../../domain/usecase/FetchVoucherDetailUseCase";
import { CreateEWalletConversionUseCase } from "../../domain/usecase/CreateEWalletConversionUseCase";
import { CreatePointConversionRateUseCase } from "../../domain/usecase/CreatePointConversionRateUseCase";
import { ImportVoucherkuUseCase } from "../../domain/usecase/ImportVoucherkuUseCase";

export type RewardFormData = {
  type: string;
  title: string;
  category: string;
  description: string;
  termsAndConditions: string;
  bannerImage: string;
  pointsAmount: string;
  quantity: string;
  validityPeriod: string;
  merchant: string;
  startDate: string;
  endDate: string;
  voucherCode: string;
  pointProvider: string;
  eWallet: string;
  voucherkuItem: string;
  voucherkuCategoryId: string;
};

export const initialFormData: RewardFormData = {
  type: RewardType.VOUCHER,
  title: "",
  category: "",
  description: "",
  termsAndConditions: "",
  bannerImage: "",
  pointsAmount: "",
  quantity: "",
  validityPeriod: "",
  merchant: "",
  startDate: "",
  endDate: "",
  voucherCode: "",
  pointProvider: "",
  eWallet: "",
  voucherkuItem: "",
  voucherkuCategoryId: "",
};

export type RewardFormErrors = Partial<Record<keyof RewardFormData, string>>;

export interface RewardCatalogCreateViewModelParams {
  setFormData: Dispatch<SetStateAction<RewardFormData>>;
  setOriginalData: Dispatch<SetStateAction<VoucherDetail | null>>;
  setErrors: Dispatch<SetStateAction<RewardFormErrors>>;
  setShowSuccessModal: Dispatch<SetStateAction<boolean>>;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setSubmitError: Dispatch<SetStateAction<string | null>>;
  setVoucherCodes: Dispatch<SetStateAction<readonly string[]>>;
}

/**
 * Calculate validity period in days from start and end dates.
 * Returns empty string if either date is missing or invalid.
 */
export const calculateValidityPeriod = (start: string, end: string): string => {
  if (!start || !end) return "";

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return "";

  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "";

  return String(diffDays);
};

/**
 * Map voucher detail API response to form data
 */
export const mapVoucherDetailToFormData = (
  detail: VoucherDetail
): RewardFormData => {
  const startDateStr = detail.validFrom?.split("T")[0] ?? "";
  const endDateStr = detail.validUntil?.split("T")[0] ?? "";
  return {
    type: detail.provider === 'voucherku' ? RewardType.VOUCHERKU : RewardType.VOUCHER,
    title: detail.title ?? "",
    category: detail.categoryId ?? "",
    description: detail.description ?? "",
    termsAndConditions: detail.termsAndConditions?.join("\n") ?? "",
    bannerImage: detail.imageUrls?.[0] ?? "",
    pointsAmount: String(detail.pointsRequired ?? 0),
    quantity: String(detail.totalStock ?? 0),
    validityPeriod: calculateValidityPeriod(startDateStr, endDateStr),
    merchant: detail.merchantId ?? "",
    startDate: startDateStr,
    endDate: endDateStr,
    voucherCode: "",
    pointProvider: "",
    eWallet: "",
    voucherkuItem: detail.categoryId ?? "",
    voucherkuCategoryId: detail.categoryId ?? "",
  };
};

export const RewardCatalogCreateViewModel = ({
  setFormData,
  setOriginalData,
  setErrors,
  setShowSuccessModal,
  setIsSubmitting,
  setIsLoading,
  setSubmitError,
  setVoucherCodes,
}: RewardCatalogCreateViewModelParams) => {
  const fetchVoucherDetail = async (voucherId: string): Promise<void> => {
    setIsLoading(true);
    setSubmitError(null);

    try {
      const detail = await FetchVoucherDetailUseCase(voucherId);
      setOriginalData(detail);
      setFormData(mapVoucherDetailToFormData(detail));
    } catch (error) {
      console.error("Failed to fetch voucher detail:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to load reward details. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    errors: RewardFormErrors,
    field: keyof RewardFormData,
    value: string
  ): void => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "startDate" || field === "endDate") {
        const newStartDate = field === "startDate" ? value : prev.startDate;
        const newEndDate = field === "endDate" ? value : prev.endDate;
        updated.validityPeriod = calculateValidityPeriod(newStartDate, newEndDate);
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTypeChange = (type: string): void => {
    setFormData((prev) => ({ ...prev, type }));
  };

  const validateForm = (formData: RewardFormData): boolean => {
    const newErrors: RewardFormErrors = {};

    if (formData.type !== RewardType.VOUCHERKU && !formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (formData.type === RewardType.VOUCHER) {
      validateVoucherFields(formData, newErrors);
    } else if (formData.type === RewardType.E_WALLET_BALANCE) {
      validateEWalletFields(formData, newErrors);
    } else if (formData.type === RewardType.POINT_CONVERSION) {
      validatePointConversionFields(formData, newErrors);
    } else if (formData.type === RewardType.VOUCHERKU) {
      validateVoucherkuFields(formData, newErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async (
    formData: RewardFormData,
    originalData: VoucherDetail | null,
    voucherCodes: readonly string[],
    isEditMode: boolean,
    voucherId?: string
  ): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditMode && voucherId) {
        const payload = buildUpdatePayload(formData, originalData);
        await UpdateVoucherUseCase(voucherId, payload);
      } else {
        await submitCreateForm(formData, originalData, voucherCodes);
      }
      setShowSuccessModal(true);
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} reward:`,
        error
      );
      setSubmitError(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} reward. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateVoucherCodesFromValidation = (
    result: { validCodes: number; voucherCodes: readonly string[] } | null
  ): void => {
    if (result && result.validCodes > 0) {
      setFormData((prev) => ({
        ...prev,
        quantity: String(result.validCodes),
      }));
      setVoucherCodes(result.voucherCodes);
    } else if (!result) {
      setFormData((prev) => ({
        ...prev,
        quantity: "",
      }));
      setVoucherCodes([]);
    }
  };

  return {
    fetchVoucherDetail,
    handleInputChange,
    handleTypeChange,
    validateForm,
    submitForm,
    updateVoucherCodesFromValidation,
  };
};

// Validation helper functions
const validateVoucherFields = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.description.trim()) {
    errors.description = "Description is required";
  }
  if (!formData.category.trim()) {
    errors.category = "Category is required";
  }
  if (!formData.termsAndConditions.trim()) {
    errors.termsAndConditions = "Terms & Conditions is required";
  }
  if (!formData.bannerImage.trim()) {
    errors.bannerImage = "Banner image is required";
  }
  if (!formData.voucherCode.trim()) {
    errors.voucherCode = "Voucher code is required";
  }
  validatePointsAmount(formData, errors, "Points amount");
  validateQuantity(formData, errors);
  validateValidityPeriod(formData, errors);
  if (!formData.merchant.trim()) {
    errors.merchant = "Merchant is required";
  }
  validateDateRange(formData, errors);
};

const validateEWalletFields = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.eWallet.trim()) {
    errors.eWallet = "E-Wallet is required";
  }
  validatePointsAmount(formData, errors, "Amount");
  validateDateRange(formData, errors);
};

const validatePointConversionFields = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.pointProvider.trim()) {
    errors.pointProvider = "Point provider is required";
  }
  validatePointsAmount(formData, errors, "Amount");
  validateDateRange(formData, errors);
};

const validateVoucherkuFields = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.voucherkuItem.trim()) {
    errors.voucherkuItem = "Voucherku item is required";
  }
  if (!formData.title.trim()) {
    errors.title = "Title is required";
  }
  if (!formData.description.trim()) {
    errors.description = "Description is required";
  }
  if (!formData.termsAndConditions.trim()) {
    errors.termsAndConditions = "Terms & Conditions is required";
  }
  if (!formData.bannerImage.trim()) {
    errors.bannerImage = "Banner image is required";
  }
  validatePointsAmount(formData, errors, "Points amount");
  validateQuantity(formData, errors);
  validateValidityPeriod(formData, errors);
  if (!formData.merchant.trim()) {
    errors.merchant = "Merchant is required";
  }
  validateDateRange(formData, errors);
};

const validatePointsAmount = (
  formData: RewardFormData,
  errors: RewardFormErrors,
  fieldLabel: string
): void => {
  if (!formData.pointsAmount.trim()) {
    errors.pointsAmount = `${fieldLabel} is required`;
  } else if (
    Number.isNaN(Number(formData.pointsAmount)) ||
    Number(formData.pointsAmount) <= 0
  ) {
    errors.pointsAmount = `${fieldLabel} must be a positive number`;
  }
};

const validateQuantity = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.quantity.trim()) {
    errors.quantity = "Quantity is required";
  } else if (
    Number.isNaN(Number(formData.quantity)) ||
    Number(formData.quantity) <= 0
  ) {
    errors.quantity = "Quantity must be a positive number";
  }
};

const validateValidityPeriod = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (formData.startDate && formData.endDate && !formData.validityPeriod) {
    errors.validityPeriod = "End date must be after or equal to start date";
  }
};

const validateDateRange = (
  formData: RewardFormData,
  errors: RewardFormErrors
): void => {
  if (!formData.startDate) {
    errors.startDate = "Start date is required";
  }
  if (!formData.endDate) {
    errors.endDate = "End date is required";
  }
  if (
    formData.startDate &&
    formData.endDate &&
    formData.startDate > formData.endDate
  ) {
    errors.endDate = "End date must be after start date";
  }
};

// Payload builder functions
const buildVoucherCreatePayload = (
  formData: RewardFormData,
  originalData: VoucherDetail | null,
  voucherCodes: readonly string[]
): CreateVoucherPayload => {
  return {
    merchantId: formData.merchant,
    categoryId: formData.category,
    pointsRequired: Number(formData.pointsAmount) || 0,
    title: formData.title,
    description: formData.description,
    totalStock: Number(formData.quantity) || 0,
    validFrom: formData.startDate
      ? new Date(formData.startDate).toISOString()
      : "",
    validUntil: formData.endDate
      ? new Date(formData.endDate).toISOString()
      : "",
    imageUrls: formData.bannerImage ? [formData.bannerImage] : [],
    termsAndConditions: formData.termsAndConditions
      ? formData.termsAndConditions.split("\n").filter((line) => line.trim())
      : [],
    howToUse: originalData?.howToUse || [],
    voucherCodes: voucherCodes,
    isPopular: originalData?.isPopular || false,
    badgeText: originalData?.badgeText || "",
    badgeColor: originalData?.badgeColor || "",
    status: originalData?.status || VoucherStatus.DRAFT,
  };
};

const buildEWalletCreatePayload = (formData: RewardFormData): CreateEWalletPayload => {
  return {
    providerId: formData.eWallet,
    pointsRequired: Number(formData.pointsAmount) || 0,
    rupiahValue: Number(formData.pointsAmount) || 0,
    dailyLimitPerUser: 200000,
    isActive: true,
    validFrom: formData.startDate
      ? new Date(formData.startDate).toISOString()
      : new Date().toISOString(),
    validUntil: formData.endDate
      ? new Date(formData.endDate).toISOString()
      : new Date().toISOString(),
  };
};

const buildPointConversionCreatePayload = (
  formData: RewardFormData
): CreatePointConversionPayload => {
  return {
    fromPartnerId: "swarapoint",
    toPartnerId: formData.pointProvider,
    fromPoints: Number(formData.pointsAmount) || 0,
    toPoints: Number(formData.pointsAmount) || 0,
    isActive: true,
    validUntil: formData.endDate
      ? new Date(formData.endDate).toISOString()
      : new Date().toISOString(),
  };
};

const buildVoucherkuCreatePayload = (
  formData: RewardFormData
): ImportVoucherkuPayload => {
  return {
    categoryId: formData.voucherkuCategoryId,
    merchantId: formData.merchant,
    pointsRequired: Number(formData.pointsAmount) || 0,
    productCode: formData.voucherkuItem,
    imageUrl: formData.bannerImage,
    termsAndConditions: formData.termsAndConditions
      ? formData.termsAndConditions.split("\n").filter((line) => line.trim())
      : [],
    validFrom: formData.startDate
      ? new Date(formData.startDate).toISOString()
      : "",
    validUntil: formData.endDate
      ? new Date(formData.endDate).toISOString()
      : "",
    title: formData.title,
    description: formData.description,
    isPopular: false,
    badgeText: "",
    badgeColor: "",
    status: VoucherStatus.DRAFT,
    providerPriority: 100,
    pointsMultiplier: 1.0,
  };
};

const buildUpdatePayload = (
  formData: RewardFormData,
  originalData: VoucherDetail | null
): UpdateVoucherPayload => {
  return {
    title: formData.title,
    description: formData.description,
    categoryId: formData.category,
    pointsRequired: Number(formData.pointsAmount) || 0,
    totalStock: Number(formData.quantity) || 0,
    imageUrls: formData.bannerImage ? [formData.bannerImage] : [],
    termsAndConditions: formData.termsAndConditions
      ? formData.termsAndConditions.split("\n").filter((line) => line.trim())
      : [],
    howToUse: originalData?.howToUse || [],
    validFrom: formData.startDate
      ? new Date(formData.startDate).toISOString()
      : "",
    validUntil: formData.endDate
      ? new Date(formData.endDate).toISOString()
      : "",
    isPopular: originalData?.isPopular || false,
    badgeText: originalData?.badgeText || "",
    badgeColor: originalData?.badgeColor || "",
    status: originalData?.status || VoucherStatus.DRAFT,
  };
};

const submitCreateForm = async (
  formData: RewardFormData,
  originalData: VoucherDetail | null,
  voucherCodes: readonly string[]
): Promise<void> => {
  switch (formData.type) {
    case RewardType.VOUCHER: {
      const payload = buildVoucherCreatePayload(formData, originalData, voucherCodes);
      await CreateVoucherUseCase(payload);
      break;
    }
    case RewardType.E_WALLET_BALANCE: {
      const payload = buildEWalletCreatePayload(formData);
      await CreateEWalletConversionUseCase(payload);
      break;
    }
    case RewardType.POINT_CONVERSION: {
      const payload = buildPointConversionCreatePayload(formData);
      await CreatePointConversionRateUseCase(payload);
      break;
    }
    case RewardType.VOUCHERKU: {
      const payload = buildVoucherkuCreatePayload(formData);
      await ImportVoucherkuUseCase(payload);
      break;
    }
    default: {
      throw new Error(`Unsupported reward type: ${formData.type}`);
    }
  }
};

